import { createApi } from '../http/http-common';
import {
  Artwork,
  ArtworkResult,
  VFileUploaderFile,
} from '@/components/w2pFileUploader';
import { AxiosResponse } from 'axios';
import {
  ChangeColorCommand,
  ChangeColorResult,
  RemoveColorCommand,
  ArtworkBackgroundResult,
  RemoveColorResult,
  DetectColorsResult,
} from '@/web-components/import-wizard/typings';
import {
  authHandler,
  invalidTokenHandler,
  validTokenHandler,
} from './interceptor-handlers/AuthHandler';
import {
  SetArtworkBackgroundCommand,
  DoRoundCroppingCommand,
  DoCroppingCommand,
  ChangeColorsCommand,
  RoundCroppingResult,
  CroppingResult,
  ChangeColorsResult,
} from '@/web-components/import-wizard/typings/colorManagement/colorManagement';
import {
  CheckImageSmallDetailsCommand,
  ProcessTextCommand,
  SmallDetailsResult,
  Product3DPreviewCommand,
  Product3DPreviewResult,
  ProductAIPreviewCommand,
  ProductAIPreviewResult,
  VectorizeResponse,
} from '@/typings/SmallDetails';
import { PmsColor } from '@midocean/webapps-shared-domain';
import { ProcessTextResult } from '@/components/w2pFileUploader/typings/Artwork';
import { SetEmbroideryEffectResult } from '@/web-components/import-wizard-v2/typings/shapeManagement/shapeManagment';

const imageProcessingAPI = createApi({
  BASE_URL: process.env.VUE_APP_IMAGE_PROCESSING_BASE_URL,
});

// Setup interceptors for the ImageProccesing API
imageProcessingAPI.interceptors.request.use(authHandler, function (error) {
  return Promise.reject(error);
});
imageProcessingAPI.interceptors.response.use(
  validTokenHandler,
  invalidTokenHandler
);

export default {
  upload(
    data: Artwork,
    file: VFileUploaderFile
  ): Promise<AxiosResponse<ArtworkResult>> {
    const formData = new FormData();
    formData.append('file', file.file);
    formData.append('json', JSON.stringify(data));
    return imageProcessingAPI.post(`artworks/processArtwork`, formData, {
      headers: {
        'Content-type': 'multipart/form-data',
      },
    });
  },

  textToImage(
    command: ProcessTextCommand
  ): Promise<AxiosResponse<ProcessTextResult>> {
    return imageProcessingAPI.post(`artworks/processTextV2`, command);
  },

  roundCropping(
    pngFile: string | Blob,
    command: DoRoundCroppingCommand
  ): Promise<AxiosResponse<RoundCroppingResult>> {
    const formData = new FormData();
    formData.append('file', pngFile);
    formData.append('json', JSON.stringify(command));
    return imageProcessingAPI.post('artworks/roundCropping', formData);
  },

  cropping(
    pngFile: string | Blob,
    command: DoCroppingCommand
  ): Promise<AxiosResponse<CroppingResult>> {
    const formData = new FormData();
    formData.append('file', pngFile);
    formData.append('json', JSON.stringify(command));
    return imageProcessingAPI.post('artworks/cropping', formData);
  },

  changeLogoBackground(
    pngFile: string | Blob,
    command: SetArtworkBackgroundCommand
  ): Promise<AxiosResponse<ArtworkBackgroundResult>> {
    const formData = new FormData();
    formData.append('file', pngFile);
    formData.append('json', JSON.stringify(command));
    return imageProcessingAPI.post('artworks/setBackground', formData);
  },
  getAvailablePmsColors(
    techniqueGroup?: string
  ): Promise<AxiosResponse<PmsColor[]>> {
    if (techniqueGroup) {
      return imageProcessingAPI.get(
        `availablePmsColors?techniqueGroupCode=` + techniqueGroup
      );
    } else {
      return imageProcessingAPI.get(`availablePmsColors`);
    }
  },

  replaceColors(
    command: ChangeColorsCommand,
    file: Blob
  ): Promise<AxiosResponse<string>> {
    const formData = new FormData();
    formData.append('file', file, `file`);
    formData.append('command', JSON.stringify(command));
    return imageProcessingAPI.post(`artworks/ChangeColors`, formData, {
      headers: {
        'Content-type': 'multipart/form-data',
      },
    });
  },

  replaceColorsV2(
    command: ChangeColorsCommand,
    files: Blob[]
  ): Promise<AxiosResponse<ChangeColorsResult[]>> {
    const formData = new FormData();

    files.forEach((file, index) => {
      formData.append('files', file, `file-${index + 1}`);
    });

    formData.append('command', JSON.stringify(command));

    return imageProcessingAPI.post(`artworks/ChangeColorsV2`, formData, {
      headers: {
        'Content-type': 'multipart/form-data',
      },
    });
  },

  replaceColor(
    command: ChangeColorCommand,
    files: Blob[]
  ): Promise<AxiosResponse<ChangeColorResult>> {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append('files', file, `file-${index + 1}`);
    });
    formData.append('command', JSON.stringify(command));
    return imageProcessingAPI.post(`artworks/ChangeColor`, formData, {
      headers: {
        'Content-type': 'multipart/form-data',
      },
    });
  },

  detectColors(
    file: Blob,
    doTrimming: boolean,
    isOriginalLogoVectorFile: boolean
  ): Promise<AxiosResponse<DetectColorsResult>> {
    const formData = new FormData();

    formData.append('doTrimming', doTrimming.toString());
    formData.append(
      'isOriginalLogoVectorFile',
      isOriginalLogoVectorFile.toString()
    );
    formData.append('file', file, 'cropped-image.png');

    return imageProcessingAPI.post(`artworks/DetectColors`, formData, {
      headers: {
        'Content-type': 'multipart/form-data',
      },
    });
  },
  async removeColor(
    command: RemoveColorCommand,
    files: Blob[]
  ): Promise<AxiosResponse<RemoveColorResult[]>> {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append('files', file, `file-${index + 1}`);
    });
    formData.append('command', JSON.stringify(command));
    const result = await imageProcessingAPI.post(
      `artworks/RemoveColor`,
      formData,
      {
        headers: {
          'Content-type': 'multipart/form-data',
        },
      }
    );
    return result;
  },
  applyEmbroideryEffect(
    pngFile: Blob | string,
    config: string
  ): Promise<AxiosResponse<SetEmbroideryEffectResult>> {
    const formData = new FormData();
    formData.append('file', pngFile || '');
    formData.append('command', config);

    const result: Promise<AxiosResponse<SetEmbroideryEffectResult>> =
      imageProcessingAPI.post(`artworks/SetEmbroideryEffect`, formData, {
        headers: {
          'Content-type': 'multipart/form-data',
        },
      });
    return result;
  },
  applyLaserEffect(pngFile: Blob | string, config: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', pngFile || '');
    formData.append('command', config);

    const result: Promise<any> = imageProcessingAPI.post(
      `artworks/SetLaserEffect`,
      formData,
      {
        headers: {
          'Content-type': 'multipart/form-data',
        },
      }
    );
    return result;
  },

  applyDebossingEffect(pngFile: Blob | string, command: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', pngFile || '');
    formData.append('command', command);

    const result: Promise<any> = imageProcessingAPI.post(
      `artworks/SetDebossingEffect`,
      formData,
      {
        headers: {
          'Content-type': 'multipart/form-data',
        },
      }
    );
    return result;
  },

  applySublimationEffect(
    command: string,
    artworkFile: Blob,
    backgroundFile: Blob
  ): Promise<any> {
    const formData = new FormData();
    formData.append('command', command);
    formData.append('files', artworkFile, 'artwork');
    formData.append('files', backgroundFile, 'background');

    const result: Promise<any> = imageProcessingAPI.post(
      `artworks/SetSublimationEffect`,
      formData,
      {
        headers: {
          'Content-type': 'multipart/form-data',
        },
      }
    );
    return result;
  },

  checkImageSmallDetails(
    command: CheckImageSmallDetailsCommand,
    file: Blob
  ): Promise<AxiosResponse<SmallDetailsResult>> {
    const formData = new FormData();
    formData.append('json', JSON.stringify(command));
    formData.append('file', file);

    return imageProcessingAPI.post(`smalldetails`, formData, {
      headers: {
        'Content-type': 'multipart/form-data',
      },
    });
  },

  createProduct3DPreview(
    command: Product3DPreviewCommand,
    files: File[]
  ): Promise<AxiosResponse<Product3DPreviewResult>> {
    const formData = new FormData();
    formData.append('command', JSON.stringify(command));
    files.forEach((file) => {
      formData.append('files', file);
    });

    return imageProcessingAPI.post(`product3DPreview`, formData, {
      headers: {
        'Content-type': 'multipart/form-data',
      },
    });
  },

  createProductAIPreview(
    command: ProductAIPreviewCommand,
    files: File[]
  ): Promise<AxiosResponse<ProductAIPreviewResult>> {
    const formData = new FormData();
    formData.append('command', JSON.stringify(command));
    files.forEach((file) => {
      formData.append('files', file);
    });

    return imageProcessingAPI.post(`productAIPreview`, formData, {
      headers: {
        'Content-type': 'multipart/form-data',
      },
    });
  },

  vectorizeImage(file: Blob): Promise<AxiosResponse<VectorizeResponse>> {
    const formData = new FormData();
    formData.append('file', file, 'image.png');

    return imageProcessingAPI.post('vectorize', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  downloadVectorizedFile(
    imageToken: string
  ): Promise<AxiosResponse<VectorizeResponse>> {
    return imageProcessingAPI.post('download', { imageToken });
  },
};
